import Dexie, { type Table } from "dexie";
import type { SyncrelState } from "../domain/types";

export interface SnapshotRecord {
  id?: number;
  room: string;
  state: SyncrelState;
  savedAt: number;
}

export interface WorkspaceRoomRecord {
  room: string;
  name: string;
  savedAt: number;
}

class SyncrelDatabase extends Dexie {
  snapshots!: Table<SnapshotRecord, number>;

  constructor() {
    super("syncrel");
    this.version(1).stores({
      snapshots: "++id, room, savedAt",
    });
  }
}

const db = new SyncrelDatabase();

export const saveSnapshot = async (room: string, state: SyncrelState): Promise<void> => {
  await db.snapshots.add({
    room,
    state,
    savedAt: Date.now(),
  });

  const oldSnapshots = await db.snapshots
    .where("room")
    .equals(room)
    .reverse()
    .offset(20)
    .toArray();

  if (oldSnapshots.length > 0) {
    await db.snapshots.bulkDelete(oldSnapshots.map((snapshot) => snapshot.id).filter((id): id is number => typeof id === "number"));
  }
};

export const loadLatestSnapshot = async (room: string): Promise<SyncrelState | null> => {
  const snapshot = await db.snapshots
    .where("room")
    .equals(room)
    .reverse()
    .first();
  return snapshot?.state ?? null;
};

export const listWorkspaceRooms = async (): Promise<WorkspaceRoomRecord[]> => {
  const snapshots = await db.snapshots.orderBy("savedAt").reverse().toArray();
  const byRoom = new Map<string, WorkspaceRoomRecord>();
  for (const snapshot of snapshots) {
    if (byRoom.has(snapshot.room)) continue;
    byRoom.set(snapshot.room, {
      room: snapshot.room,
      name: snapshot.state.workspace.name,
      savedAt: snapshot.savedAt,
    });
  }
  return Array.from(byRoom.values());
};

export const deleteWorkspaceRoom = async (room: string): Promise<void> => {
  await db.snapshots.where("room").equals(room).delete();
  await deleteIndexedDbDatabase(`syncrel:${room}`);
};

const deleteIndexedDbDatabase = (name: string): Promise<void> => new Promise((resolve) => {
  if (typeof indexedDB === "undefined") {
    resolve();
    return;
  }
  const request = indexedDB.deleteDatabase(name);
  request.onsuccess = () => resolve();
  request.onerror = () => resolve();
  request.onblocked = () => resolve();
});
